import React, { FC, useMemo } from 'react';

import {
	EvFiltersPanelProvider,
	EvFlexColumn,
	EvFlexRow,
	EvLink,
	EvSection,
	EvSpinner,
	EvTitle,
	TABLE_FILTERS_SR_SUMMARY,
	TEvFiltersPanelValues
} from '@evinced-private/ui-common';

import {
	MFA_DASHBOARD_PAGE_FILTERS_SECTION_ID,
	MFA_DASHBOARD_PAGE_MAIN_TITLE_ID,
	MFA_DASHBOARD_PAGE_OLDER_SESSIONS_LINK_ID
} from 'data-automation-ids/MfaDashboardAutomationIds';
import { MfaSessionsPageFilterDefinitions } from 'src/apps/mfa/helpers/MfaSessionsPageFilterDefinitionsHelper';
import { useMfaLabelsOptionsQuery } from 'src/apps/mfa/hooks/useMfaLabelsOptionsQuery';
import { useMfaOwnerOptionsQuery } from 'src/apps/mfa/hooks/useMfaOwnerOptionsQuery';
import {
	PinnedDateRangeFiltersPanelBar,
	ProductFiltersPanelTrigger
} from 'src/common/components/pinned-date-range-filters-panel';
import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import {
	buildPanelInitialValues,
	mapPanelValuesToFilters,
	mapToPanelDefinitions
} from 'src/common/helpers/FiltersPanelHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { MFA_SESSIONS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';

import {
	PLATFORM_OPTIONS,
	SESSIONS_PERIOD_OPTIONS,
	SEVERITY_OPTIONS
} from './MfaSessionsFilterDefinitions';
import { MFA_SESSIONS_HEADER_LABELS } from './MfaSessionsHeader.labels';

interface MfaSessionsPageHeaderProps {
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
}

const EMPTY_CREATED_BY_OPTION = { value: ' ', label: 'Unknown' };

export const MfaSessionsPageHeader: FC<MfaSessionsPageHeaderProps> = ({
	filters,
	onFilterChange
}) => {
	const ownerOptionsQuery = useMfaOwnerOptionsQuery();
	const labelOptionsQuery = useMfaLabelsOptionsQuery();

	const panelDefinitions = useMemo(
		() => mapToPanelDefinitions(MfaSessionsPageFilterDefinitions),
		[]
	);

	const initialValues = useMemo<TEvFiltersPanelValues>(
		() => buildPanelInitialValues(panelDefinitions, filters),
		[panelDefinitions, filters]
	);

	const filterOptions = useMemo(() => {
		const staticFilterOptions = {
			sessionsPeriod: SESSIONS_PERIOD_OPTIONS,
			platform: PLATFORM_OPTIONS,
			severity: SEVERITY_OPTIONS,
			owner:
				ownerOptionsQuery.data?.map((item) => {
					if (typeof item.value === 'string' && item.value === ' ') {
						return EMPTY_CREATED_BY_OPTION;
					}
					return item;
				}) ?? []
		};

		const dynamicFilterOptions = {};

		labelOptionsQuery.data?.forEach((label) => {
			dynamicFilterOptions[label.labelKey] = label.options;
		});

		return { ...staticFilterOptions, ...dynamicFilterOptions };
	}, [ownerOptionsQuery.data, labelOptionsQuery.data]);

	const handleApply = (appliedFilters: TEvFiltersPanelValues): void => {
		onFilterChange(MFA_SESSIONS_TABLE_ID, mapPanelValuesToFilters(appliedFilters));
	};

	const isLoading = ownerOptionsQuery.isLoading || labelOptionsQuery.isLoading;

	return (
		<EvFiltersPanelProvider
			appElement={PLATFORM_APP_ID}
			onApply={handleApply}
			onReset={handleApply}
			initialValues={initialValues}
			filterOptions={filterOptions}
			filterDefinitions={panelDefinitions}
		>
			<EvFlexColumn gap="XLARGE">
				<EvFlexRow align="CENTER" justify="SPACE_BETWEEN">
					<EvFlexRow align="CENTER" gap="MEDIUM" width="auto">
						<EvTitle
							titleText={MFA_SESSIONS_HEADER_LABELS.PAGE_TITLE}
							dataAutomationId={MFA_DASHBOARD_PAGE_MAIN_TITLE_ID}
							headingLevel={1}
							className="main-title page-heading"
							MoreInfoContent={MFA_SESSIONS_HEADER_LABELS.TITLE_MORE_INFO}
						/>
						<EvLink
							url={RoutesHelper.getMfaV1DashboardPath()}
							linkText={MFA_SESSIONS_HEADER_LABELS.OLDER_SESSIONS_LINK}
							showIcon
							dataAutomationId={MFA_DASHBOARD_PAGE_OLDER_SESSIONS_LINK_ID}
						/>
					</EvFlexRow>
					<ProductFiltersPanelTrigger />
				</EvFlexRow>
				<EvSection
					ariaLabel={MFA_SESSIONS_HEADER_LABELS.FILTERS_SECTION_ARIA_LABEL}
					ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
					ariaLive="polite"
					skipLinkId="filters-section"
					dataAutomationId={MFA_DASHBOARD_PAGE_FILTERS_SECTION_ID}
				>
					{!isLoading ? (
						<PinnedDateRangeFiltersPanelBar
							panelDefinitions={panelDefinitions}
							appliedValues={initialValues}
							onApply={handleApply}
						/>
					) : (
						<EvSpinner />
					)}
				</EvSection>
			</EvFlexColumn>
		</EvFiltersPanelProvider>
	);
};
